import React, { FC, useEffect, useState } from "react";
import { PDFViewer, PDFViewerTool } from "@progress/kendo-react-pdf-viewer";
import { useParams } from "react-router";
import {
	getPdfApi,
	getPickListReportApi,
	getSoldFrozenWeeklyReportApi,
	getSoldFrozenDailyReportApi,
	getPurchaseStatisticsReportApi,
	getSalesNoteListReportApi,
	getSoldFreshReportApi,
	getSalesNoteJournalReportApi,
	getAccountStatementReportApi,
} from "api/pdf";
import { PickListFilter } from "api/pickList";
import { SalesNoteJournalFilter } from "api/sales";
import { SalesNoteFilter } from "api/salesNote";
import { useInfoStore } from "store/error";
import { Loader } from "@progress/kendo-react-indicators";

const tools: PDFViewerTool[] = [
	"pager",
	"spacer",
	"zoomInOut",
	"zoom",
	"selection",
	"spacer",
	"search",
	"download",
	"print",
];

export const PdfViewer: FC<{}> = ({ }) => {
	const { id, type, guid } = useParams();
	const [pdfData, setPdfData] = useState<string>();
	const [loading, setLoading] = useState<boolean>(false);
	const [saveFileName, setSaveFileName] = useState<string>("document.pdf");
	const { setError } = useInfoStore();
	const [filterOptions, setFilterOptions] = useState<PickListFilter>({
		take: 999999,
		skip: 0,
	});

	useEffect(() => {
		if (type !== null && type !== undefined) {
			const storedFromDate = localStorage.getItem("fromDate");
			const storedToDate = localStorage.getItem("toDate");
			filterOptions.fromDate = storedFromDate ? new Date(storedFromDate) : undefined;
			filterOptions.toDate = storedToDate ? new Date(storedToDate) : undefined;
			filterOptions.buyerId = localStorage.getItem("buyerId") ?? undefined;
			filterOptions.vesselId = localStorage.getItem("vesselId") ?? undefined;
			filterOptions.vesselRegistrationNumber = localStorage.getItem("vesselRegistrationNumber") ?? undefined;
			filterOptions.fishingGearCode = localStorage.getItem("vesselRegistrationNumber") ?? undefined;
			filterOptions.catchAreaCode = localStorage.getItem("vesselRegistrationNumber") ?? undefined;

			if (type === "1") {
				setSaveFileName("PickList.pdf");
				setLoading(true);
				getPickListReportApi(filterOptions)
					.then((response) => { setPdfData(response); })
					.catch((err) => {
						setError(err?.response?.data ? err.response.data.returnText : err.message);
					})
					.finally(() => { setLoading(false); });
			}
			else if (type === "2") {
				setSaveFileName("SoldFrozenWeekly.pdf");
				setLoading(true);
				getSoldFrozenWeeklyReportApi(filterOptions)
					.then((response) => { setPdfData(response); })
					.catch((err) => {
						setError(err?.response?.data ? err.response.data.returnText : err.message);
					})
					.finally(() => { setLoading(false); });
			}
			else if (type === "3") {
				setSaveFileName("SoldFrozenDaily.pdf");
				setLoading(true);
				getSoldFrozenDailyReportApi(filterOptions)
					.then((response) => { setPdfData(response); })
					.catch((err) => {
						setError(err?.response?.data ? err.response.data.returnText : err.message);
					})
					.finally(() => { setLoading(false); });
			}
			else if (type === "4") {
				setSaveFileName("PurchaseStatistics.pdf");
				setLoading(true);
				getPurchaseStatisticsReportApi(filterOptions)
					.then((response) => { setPdfData(response); })
					.catch((err) => {
						setError(err?.response?.data ? err.response.data.returnText : err.message);
					})
					.finally(() => { setLoading(false); });
			}
			else if (type === "5") {
				setSaveFileName("SoldFresh.pdf");
				setLoading(true);
				getSoldFreshReportApi(filterOptions)
					.then((response) => { setPdfData(response); })
					.catch((err) => {
						setError(err?.response?.data ? err.response.data.returnText : err.message);
					})
					.finally(() => { setLoading(false); });
			}
			else if (type === "6") {
				setSaveFileName("SalesNoteJournal.pdf");
				setLoading(true);
				const journalFilter: SalesNoteJournalFilter = {
					dateFrom: storedFromDate ? new Date(storedFromDate) : undefined,
					dateTo: storedToDate ? new Date(storedToDate) : undefined,
					byerLedgerAccount: localStorage.getItem("byerLedgerAccount") ? Number(localStorage.getItem("byerLedgerAccount")) : undefined,
					terminal: localStorage.getItem("terminal") ?? undefined,
					species: localStorage.getItem("species") ?? undefined,
					fishingGearCode: localStorage.getItem("fishingGearCode") ?? undefined,
					catchField1: localStorage.getItem("catchField1") ?? undefined,
					catchField2: localStorage.getItem("catchField2") ?? undefined,
					conditionCode: localStorage.getItem("conditionCode") ?? undefined,
					preservationCode: localStorage.getItem("preservationCode") ?? undefined,
					fishermanLedgerAccount: localStorage.getItem("fishermanLedgerAccount") ?? undefined,
					orderBy: localStorage.getItem("orderBy") ?? undefined,
				};
				getSalesNoteJournalReportApi(journalFilter)
					.then((response) => { setPdfData(response); })
					.catch((err) => {
						setError(err?.response?.data ? err.response.data.returnText : err.message);
					})
					.finally(() => { setLoading(false); });
			}
			else if (type === "7") {
				setSaveFileName("SalesNoteList.pdf");
				setLoading(true);
				getSalesNoteListReportApi(filterOptions)
					.then((response) => { setPdfData(response); })
					.catch((err) => {
						setError(err?.response?.data ? err.response.data.returnText : err.message);
					})
					.finally(() => { setLoading(false); });
			}
			else if (type === "8") {
				setSaveFileName("AccountStatement.pdf");
				setLoading(true);
				const accountStatementFilter = {
					buyerLedgerAccount: localStorage.getItem("buyerLedgerAccount") ? Number(localStorage.getItem("buyerLedgerAccount")) : undefined,
					year: localStorage.getItem("year") ? Number(localStorage.getItem("year")) : new Date().getFullYear(),
					periodFrom: localStorage.getItem("periodFrom") ? Number(localStorage.getItem("periodFrom")) : undefined,
					periodTo: localStorage.getItem("periodTo") ? Number(localStorage.getItem("periodTo")) : undefined,
				};
				getAccountStatementReportApi(accountStatementFilter)
					.then((response) => { setPdfData(response); })
					.catch((err) => {
						setError(err?.response?.data ? err.response.data.returnText : err.message);
					})
					.finally(() => { setLoading(false); });
			}

		} else {
			if (guid) {
				const noteNumber = localStorage.getItem("noteNumber");
				const pdfType = localStorage.getItem("pdfType");
				let filename = "Document.pdf";

				if (pdfType === "SalesNote") {
					filename = "Sluttseddel_" + noteNumber + ".pdf";
				}
				else if (pdfType === "LandingNote") {
					filename = "Landingsseddel_" + noteNumber + ".pdf";
				}
				else if (pdfType === "SalesConfirmation") {
					filename = "Salgsbekreftelse_" + noteNumber + ".pdf";
				}
				else if (pdfType === "Contract") {
					filename = "Kontrakt_" + noteNumber + ".pdf";
				}
				else if (pdfType === "Invoice") {
					filename = "Faktura_" + noteNumber + ".pdf";
				}
				else if (pdfType === "CreditNote") {
					filename = "Kreditnota_" + noteNumber + ".pdf";
				}
				else if (pdfType === "Interest") {
					filename = "Rentenota_" + noteNumber + ".pdf";
				}
				else if (pdfType === "Settlement") {
					filename = "Avregning_" + noteNumber + ".pdf";
				}
				else if (pdfType === "YearlyRevenue") {
					filename = "Aarsrapport_" + noteNumber + ".pdf";
				}

				setSaveFileName(filename);
				setLoading(true);

				getPdfApi(guid)
					.then((response) => { setPdfData(response); })
					.catch((err) => {
						setError(err?.response?.data ? err.response.data.returnText : err.message);
					})
					.finally(() => {
						setLoading(false);
						localStorage.removeItem("noteNumber");
						localStorage.removeItem("pdfType");

					});

				return;
			}
		}
	}, []);

	if (loading) {
		return (
			<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
				<Loader size="large" type="converging-spinner" />
			</div>
		);
	}

	return <PDFViewer data={pdfData} style={{ height: "100%" }} tools={tools} saveFileName={saveFileName} />;
};

export default PdfViewer;